import { ButtonGradientPrimary, ButtonSecondary, ModalDialog } from '@amelio/ui-library';

import type { MeetingTemplate } from '@/types/thematic-meetings';

import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { TemplatePreviewList } from '@/pages/ManageTemplates/components/AmelioActiveTemplates/TemplatePreviewList';
import { getTextByLanguage } from '@/utils/localization';

type MeetingPlanPreviewModalProps = {
    /** Kept by the caller after the preview closes, so its content stays in place while it fades. */
    template: MeetingTemplate | undefined;
    isOpen: boolean;
    isImporting: boolean;
    onClose: () => void;
    onImport: () => void;
};

/**
 * One plan of the Amelio catalogue, read before it is imported: what the conversation is for and the
 * subjects it runs through. The only place a catalogue plan is imported from, as with the evaluation
 * templates.
 */
export const MeetingPlanPreviewModal = ({
    template,
    isOpen,
    isImporting,
    onClose,
    onImport,
}: MeetingPlanPreviewModalProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const agendaItems = template?.agendaItems ?? [];

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={onClose}
            closeAriaLabel={t('common.close')}
            title={template ? getTextByLanguage(template.title, language) : ''}
            subtitle={template ? getTextByLanguage(template.objective, language) : undefined}
            headingLevel={4}
            size={'md'}
            backdropBlur
            dataTestId={'meeting-plan-preview-modal'}
            footer={
                <div className={'flex items-center justify-end gap-3'}>
                    <ButtonSecondary onClick={onClose}>{t('common.close')}</ButtonSecondary>

                    <ButtonGradientPrimary
                        onClick={onImport}
                        loading={isImporting}
                    >
                        {t(
                            'templateManagement.template.importTemplateQuestionModal.importTemplateButton',
                        )}
                    </ButtonGradientPrimary>
                </div>
            }
        >
            <TemplatePreviewList
                heading={t('templateManagement.template.meetingPlans.catalog.agendaHeading')}
                items={agendaItems.map((item) => getTextByLanguage(item.title, language))}
                emptyMessage={t('templateManagement.template.meetingPlans.catalog.noAgenda')}
            />
        </ModalDialog>
    );
};
